import React, { FC } from 'react';
import { ArrowTopRightOnSquareIcon, XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { MegaMenuProps, Tile } from 'components/commercetools-ui/organisms/header/types';
import useClassNames from 'helpers/hooks/useClassNames';
import { Category } from 'types/entity/category';
import { LinkReference, Reference } from 'types/reference';
import BgIcon from '../bg-icon';

export interface Props {
  parentLink: Category;
  show: boolean | undefined;
  links: Category[];
  tileContent?: Tile;
  onClick?: () => void;
  megaMenuData?: MegaMenuProps;
}

const MenuDropdown: FC<Props> = ({ parentLink, show, links, tileContent, onClick, megaMenuData }) => {
  const wrapperClassNames = useClassNames([
    'xl:max-w-[1440] transition absolute flex bottom-0 left-[50%] duration-300 ease-in-out min-h-[300px] w-[90vw] 2xl:w-[80vw] translate-y-full translate-x-[-50%] shadow-lg rounded-b-[16px] flex-col',
    show ? 'opacity-1 z-20 delay-300' : 'opacity-0 z-[-999] pointer-events-none',
    'border-t-[1px] border-t-gray-500 bg-white pl-52 pr-48 py-32',
  ]);

  return (
    <div className={wrapperClassNames}>
      <div className="mb-24 flex justify-between">
        <div onClick={onClick}>
          <Link link={parentLink?._url} title={parentLink?.name}>
            <Typography as="span">{parentLink?.name}</Typography>
          </Link>
        </div>
        <button type="reset" onClick={onClick}>
          <CloseIcon className="w-24 fill-primary-black" />
        </button>
      </div>
      <div className="flex justify-between">
        <div className="w-3/4">
          {megaMenuData?.category && (
            <Link link={megaMenuData?.categoryLink} variant="menu-header" className="whitespace-nowrap">
              <div className="mb-10 text-14">
                {megaMenuData?.category}
                {megaMenuData?.categoryLink?.openInNewWindow && (
                  <ArrowTopRightOnSquareIcon className="ml-4 size-14 stroke-gray-400 group-hover:stroke-white" />
                )}
              </div>
            </Link>
          )}

          <div className="grid grid-cols-3 gap-8">
            {megaMenuData?.subcategories?.map((subcategory, index) => {
              const subcategoryUrl =
                subcategory.url?.type === 'link'
                  ? `${(subcategory.url as LinkReference).link}${subcategory.key}`
                  : (subcategory.url as Reference);
              return (
                <div key={index} onClick={onClick} className="w-min pb-8">
                  <Link link={subcategoryUrl} variant="menu-item" className="group whitespace-nowrap">
                    <div className="text-14">
                      {subcategory.name}
                      {subcategory?.url?.openInNewWindow && (
                        <ArrowTopRightOnSquareIcon className="ml-4 inline size-14 stroke-gray-400 align-baseline group-hover:stroke-gray-800" />
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

          <div>
            <Link link={megaMenuData?.categoryLink} variant="menu-header" className="whitespace-nowrap">
              <Typography className="mt-40 text-14 text-blue-600 underline">
                {`All ${megaMenuData?.category}`}
              </Typography>
            </Link>
          </div>
        </div>

        <div className="relative z-10 h-fit max-w-350 border-l border-gray-300 pl-24">
          <p className="text-base font-medium text-orange-600">{megaMenuData?.spotlightHeader ?? 'In the spotlight'}</p>
          {megaMenuData?.spotlights?.map((spotlight, index) => (
            <Link key={index} link={spotlight.link} className="group">
              <div className="border-b border-gray-300 p-16">
                <div className="pb-4 text-base font-medium group-hover:text-blue-600 group-hover:underline group-focus:text-blue-600 group-focus:underline group-active:text-blue-600 group-active:underline">
                  {spotlight.title}
                  {spotlight?.link?.openInNewWindow && (
                    <ArrowTopRightOnSquareIcon className="ml-4 inline size-14 stroke-gray-600 align-baseline group-hover:stroke-blue-600" />
                  )}
                </div>
                <Typography className="text-xs font-normal text-gray-600">{spotlight.description}</Typography>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 right-0">
        <BgIcon />
      </div>
    </div>
  );
};

export default MenuDropdown;
